import React, { useState, useEffect } from 'react';
import moment from 'moment';
import ArthaGateway from 'service/ArthaGateway';
import { CreditNote, Sale, SaleOrder, Receipt, VoucherSummary, Party, OrderStatuses, User } from 'service';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';
import { faCalendar, faCircleCheck, faF, faFileInvoice, faIndustry, faPhone, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { durations } from 'service/Utils';
import { NavLink } from 'react-router-dom';
import AsyncSelect from 'react-select/async';

export const Orders = () => {
    const userData = useSelector((state: RootState) => state.appdata);
    const [user, setUser] = useState<User>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [durationId, setDurationId] = useState<string>(moment().month() > 3 ? moment().year().toString() : moment().subtract(1, 'year').year().toString());
    const [orders, setOrders] = useState<SaleOrder[]>([]);
    const [productionPlants, setProductionPlants] = useState<any[]>([]);
    const [productionPlantId, setProductionPlantId] = useState<number>(0);
    const [partyId, setPartyId] = useState<number>(0);
    const [partyName, setPartyName] = useState<string>("");
    const [status, setStatus] = useState<number>(2);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(5);
    const [isRestoring, setIsRestoring] = useState(true);

    useEffect(() => {
        if (userData.profile) {
            setUser(userData.profile.profile);
            setIsLoading(false);
        }
    }, [userData]);

    useEffect(() => {
        if (!user) { return; }
        setProductionPlantId(user.productionPlantId);

        loadUserFilters();
        if (user.productionPlantId == 0) {
            loadProductionPlants();
        } else {
            loadOrders();
        }
    }, [user]);

    useEffect(() => {
        loadOrders();
    }, [pageNumber]);

    useEffect(() => {
        if (isRestoring) { return; }

        setOrders([]);
        if (pageNumber > 1) {
            setPageNumber(1);
        }
        else {
            loadOrders();
        }
    }, [durationId, productionPlantId, status, partyId]);

    const loadUserFilters = async () => {
        if (!user) { return; }

        setIsRestoring(true);
        const value = sessionStorage.getItem("orders_filters");
        if (value) {
            const user_filters = JSON.parse(value);
            setDurationId(user_filters.durationId);
            if(user.productionPlantId == 0) {
                setProductionPlantId(user_filters.productionPlantId);
            }
            setStatus(user_filters.status);
            setPartyId(user_filters.partyId);
            setPartyName(user_filters.partyName);
        }
        setIsRestoring(false);
    };

    const loadProductionPlants = () => {
        ArthaGateway.getProductionPlants()
            .then((data) => {
                setProductionPlants(data);
                loadOrders();
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const loadOrders = () => {        
        if (isRestoring) { return; }

        let user_filters = { durationId, productionPlantId, status, partyId, partyName };
        sessionStorage.setItem("orders_filters", JSON.stringify(user_filters));

        setIsLoading(true);        
        ArthaGateway
            .getOrders(durationId, productionPlantId, status, partyId, pageNumber, pageSize)
            .then((data) => {
                data.entries.map((order) => order.isExpanded = false);
                if (pageNumber > 1) {
                    setOrders(orders.concat(data.entries));
                }
                else {
                    setOrders(data.entries);
                }

                setTotalPages(data.totalPages);
                setIsLoading(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    const onPartySelection = selectedParty => {
        let partyId = selectedParty ? selectedParty.value : 0;
        setPartyId(partyId);
        setPartyName(selectedParty ? selectedParty.label : "");
    };

    const onSearchParty = (inputValue: string) => {
        return new Promise<any[]>(async (resolve) => {
            const data = await ArthaGateway.searchParty(inputValue);
            var mappedData = data.entries.map(function (party) {
                return {
                    value: party.id,
                    label: party.name
                };
            });
            resolve(mappedData);
        });
    };

    const currencyFormat = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(value);

    return (
        <div className='page-receipts'>
            <div className="row">
                <div className="col-12 col-md-6 col-lg-4 offset-md-3 offset-lg-4">
                    <div className="card bg-dark text-white mb-2 pb-2">
                        <div className="card-header">
                            <h2 className="fw-bold mb-4">Orders</h2>
                            <div>
                                <select className="form-control" id="duration" value={durationId} onChange={(e) => setDurationId(e.target.value)}>
                                    {durations.map((duration) => (
                                        <option key={duration.value} value={duration.value}>{duration.label}</option>
                                    ))}
                                    <option value="-1">Other</option>
                                </select>
                            </div>
                            {user && user.productionPlantId == 0 && (
                                <div className="col-12">
                                    <label className="form-label mb-0 mt-1">Assigned To</label>
                                    <select className="form-select" value={productionPlantId} onChange={(e) => setProductionPlantId(Number(e.target.value))}>
                                        <option value="0">All</option>
                                        {productionPlants.map((plant) => (
                                            <option key={plant.id} value={plant.id}>{plant.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="col-12">
                                <label className="form-label mb-0 mt-1">Status</label>
                                <select className="form-select" value={status} onChange={(e) => setStatus(Number(e.target.value))}>
                                    {user && user.roleId <= 3 && (
                                        <>
                                            <option value="0">All</option>
                                            <option value="1">Received</option>
                                        </>
                                    )}
                                    {OrderStatuses.filter(s => s.canShow).map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12">
                                <label className="form-label mb-0 mt-1">Party</label>
                                <AsyncSelect
                                    cacheOptions
                                    onChange={onPartySelection}
                                    loadOptions={onSearchParty}
                                    value={partyId == 0 ? null : { value: partyId, label: partyName }}
                                    isClearable
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    placeholder="Select a party"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row mt-2">
                <div className='col-12 col-md-6 col-lg-4 offset-md-3 offset-lg-4'>
                    {isLoading &&
                        <div className="text-center" style={{ padding: 20 }}>
                            <div className="spinner-border text-dark" role="status">
                            </div>
                        </div>
                    }
                    {orders.length === 0 && !isLoading &&
                        <div className="alert alert-danger text-center" style={{ margin: 10 }}>
                            No orders found
                        </div>
                    }
                    {orders && orders.map((order) => (
                        <div className='card my-2' key={order.id}>
                            <div className="card-header">
                                <h2 className="fw-bold m-0 float-start">{order.voucherNumber}</h2>
                                <div className="fw-bold float-end">
                                    {OrderStatuses.filter(s => s.id === order.status).map((s) => (
                                        <span key={s.id} className={s.class}>{s.name}</span>
                                    ))}
                                </div>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        <tr>
                                            <td style={{ width: "60px", fontSize: "1.5rem", textAlign: "center" }}>
                                                <FontAwesomeIcon icon={faUser} />
                                            </td>
                                            <td colSpan={2}>
                                                Party:
                                                <div className="fw-bold">{order.partyName}</div>
                                            </td>
                                        </tr>
                                        <tr className="align-middle">
                                            <td style={{ width: "60px", fontSize: "1.5rem", textAlign: "center" }}>
                                                <FontAwesomeIcon icon={faCalendar} />
                                            </td>
                                            <td colSpan={2}>
                                                Date:
                                                <div className="fw-bold">{moment(order.date).format("DD MMM YYYY")}</div>
                                            </td>
                                        </tr>
                                        {user && user.productionPlantId == 0 && (
                                            <tr className="align-middle">
                                                <td style={{ width: "60px", fontSize: "1.5rem", textAlign: "center" }}>
                                                    <FontAwesomeIcon icon={faUsers} />
                                                </td>
                                                <td colSpan={2}>
                                                    Assigned To:
                                                    <div className="fw-bold">{order.productionPlantName}</div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="card-footer">
                                <NavLink className="btn btn-dark w-100" title="View" to={`/order/${order.id}`}>
                                    <FontAwesomeIcon icon={faFileInvoice} style={{ width: "auto" }} />
                                    &nbsp; View Details
                                </NavLink>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="card-footer">
                    {(totalPages > pageNumber) && <>
                        {isLoading &&
                            <div className="btn btn-dark w-100 text-center">
                                <div className="spinner-border text-light" role="status">
                                </div>
                            </div>
                        }
                        {!isLoading &&
                            <div className="btn btn-dark w-100 text-center" onClick={() => setPageNumber(pageNumber + 1)}>Load More...</div>
                        }
                    </>
                    }
                </div>
            </div>
        </div>
    );
};